// Complaints and corrective actions register (ISO 9001 clause 10.2). Office/admin. A
// filterable list of complaints with a New Complaint modal; a row opens the detail page.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  api,
  ApiError,
  type ComplaintListItem,
  type ComplaintListFilters,
  type ComplaintSource,
  type ComplaintSeverity,
  type ComplaintStatus,
} from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { RecordPicker } from "../../components/RecordPicker";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { initialMobileLayout } from "../../lib/mobileLayout";
import {
  SOURCE_LABEL,
  SEVERITY_LABEL,
  daysBetween,
  isComplaintOverdue,
  COMPLAINT_CATEGORIES,
} from "./complaintsUi";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { EmptyState } from "../../components/EmptyState";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { ListToolbar } from "../../components/ListToolbar";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "All" },
  { value: "open", label: "Open" },
  { value: "investigating", label: "Investigating" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

// M-10, Session AQU: the fields an office user would type to find a complaint, and the sortable
// value per column. Severity and status sort by their real order (low to critical, open to
// closed), never alphabetically; dates sort as timestamps, never their formatted strings.
const SEVERITY_RANK: Record<ComplaintSeverity, number> = { low: 0, medium: 1, high: 2, critical: 3 };
const STATUS_RANK: Record<ComplaintStatus, number> = { open: 0, investigating: 1, resolved: 2, closed: 3 };
const ts = (iso: string | null): number | null => (iso ? new Date(iso).getTime() : null);
const COMPLAINT_SEARCH_ACCESSORS: SearchAccessors<ComplaintListItem> = [
  (r) => r.ref,
  (r) => r.customerName,
  (r) => r.category,
  (r) => SOURCE_LABEL[r.source],
  (r) => r.description,
];
const COMPLAINT_SORT_ACCESSORS: SortAccessors<ComplaintListItem> = {
  ref: (r) => r.ref,
  raised: (r) => ts(r.dateRaised),
  source: (r) => SOURCE_LABEL[r.source],
  category: (r) => r.category,
  severity: (r) => SEVERITY_RANK[r.severity],
  status: (r) => STATUS_RANK[r.status],
  customer: (r) => r.customerName,
  daysOpen: (r) => daysBetween(r.dateRaised, r.closedAt ?? r.resolvedAt),
  target: (r) => ts(r.targetResolutionDate),
};

const inputCls = "rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function ComplaintsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ComplaintListItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState("");
  const [severity, setSeverity] = useState("");
  const [source, setSource] = useState("");
  const [adding, setAdding] = useState(false);
  // Below md the list renders as cards instead of the table (Session AIB, mobile audit M3). The
  // choice is made once at mount from matchMedia, which jsdom does not implement, so existing
  // tests keep rendering the table they pin (src/lib/mobileLayout.ts).
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback(() => {
    setLoadError(false);
    const filters: ComplaintListFilters = {};
    if (status) filters.status = status as ComplaintStatus;
    if (severity) filters.severity = severity as ComplaintSeverity;
    if (source) filters.source = source as ComplaintSource;
    api.complaints
      .list(filters)
      .then(setRows)
      .catch(() => setLoadError(true));
  }, [status, severity, source]);

  useEffect(() => {
    load();
  }, [load]);

  // M-10, Session AQU: search and sort run client-side over the rows the server returned for the
  // status / severity / source filters, which stay server filters exactly as before. The page
  // uses no other URL param, so the search takes the hook's default ?q=. No active sort column
  // keeps the server's load order.
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("complaints");
  const shown = rows === null ? [] : sortRows(filterRows(rows, search, COMPLAINT_SEARCH_ACCESSORS), COMPLAINT_SORT_ACCESSORS, sort);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        title="Complaints"
        className="mb-4"
        subtitle="Customer complaints and corrective actions (ISO 9001 clause 10.2)."
        actions={
          <Button onClick={() => setAdding(true)} icon={<Plus aria-hidden size={15} />}>
            New complaint
          </Button>
        }
      />

      <ListToolbar
        className="mb-4"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search reference, customer or category..."
        searchLabel="Search complaints"
        filters={
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value || "all"}
                  onClick={() => setStatus(f.value)}
                  className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                    status === f.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <label className="w-full text-xs sm:w-auto">
              <span className={labelCls}>Severity</span>
              <select aria-label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value)} className={`${inputCls} w-full min-w-0 sm:w-auto`}>
                <option value="">All</option>
                {(["low", "medium", "high", "critical"] as ComplaintSeverity[]).map((s) => (
                  <option key={s} value={s}>
                    {SEVERITY_LABEL[s]}
                  </option>
                ))}
              </select>
            </label>
            <label className="w-full text-xs sm:w-auto">
              <span className={labelCls}>Source</span>
              <select aria-label="Source" value={source} onChange={(e) => setSource(e.target.value)} className={`${inputCls} w-full min-w-0 sm:w-auto`}>
                <option value="">All</option>
                {(["customer", "third_party", "internal", "regulatory"] as ComplaintSource[]).map((s) => (
                  <option key={s} value={s}>
                    {SOURCE_LABEL[s]}
                  </option>
                ))}
              </select>
            </label>
          </div>
        }
      />

      {loadError ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load complaints. Please try again.
        </p>
      ) : rows === null ? (
        <Loader label="Loading complaints..." />
      ) : rows.length === 0 ? (
        <EmptyState message="No complaints recorded." />
      ) : shown.length === 0 ? (
        <EmptyState role="status" message={`No complaints match "${search.trim()}".`} />
      ) : mobileCards ? (
        /* Session AIB, mobile audit M3. Priority hiding alone left four data columns sharing one
           phone width (about 48px of content each after padding), and this page had no card
           fallback of its own. The card brings back the two facts the hiding dropped that decide
           whether a complaint needs attention TODAY: who it is for, and whether it is overdue. */
        <MobileCardList label="Complaints">
          {shown.map((r) => {
            const overdue = isComplaintOverdue(r);
            const openDays = daysBetween(r.dateRaised, r.closedAt ?? r.resolvedAt);
            return (
              <MobileListCard
                key={r.id}
                to={`/office/complaints/${r.id}`}
                primary={r.ref}
                secondary={r.customerName || "No customer recorded"}
                meta={`Raised ${formatDate(r.dateRaised)} · ${openDays} day${openDays === 1 ? "" : "s"} open`}
                right={
                  <>
                    <StatusPill domain="complaintSeverity" status={r.severity} />
                    <StatusPill domain="complaint" status={r.status} />
                  </>
                }
                details={
                  <p className={`text-xs ${overdue ? "text-ele-danger" : "text-ele-body"}`}>
                    Target {formatDate(r.targetResolutionDate)}
                    {overdue ? " (overdue)" : ""}
                    <span className="block opacity-70">{SOURCE_LABEL[r.source]}{r.category ? ` · ${r.category}` : ""}</span>
                  </p>
                }
              />
            );
          })}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "13%" }} />
              <col style={{ width: "10%" }} />
              <col className="max-md:hidden" style={{ width: "11%" }} />
              <col className="max-md:hidden" style={{ width: "16%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col className="max-md:hidden" style={{ width: "16%" }} />
              <col className="max-md:hidden" style={{ width: "7%" }} />
              <col className="max-md:hidden" style={{ width: "11%" }} />
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
              <tr>
                <SortableHeader label="Reference" colKey="ref" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Raised" colKey="raised" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Source" colKey="source" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Category" colKey="category" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Severity" colKey="severity" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                <SortableHeader label="Customer" colKey="customer" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Days open" colKey="daysOpen" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Target" colKey="target" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const overdue = isComplaintOverdue(r);
                const openDays = daysBetween(r.dateRaised, r.closedAt ?? r.resolvedAt);
                return (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/office/complaints/${r.id}`)}
                    className="group cursor-pointer border-t border-ele-hairline hover:bg-ele-surface"
                  >
                    <td className="px-3 py-2 font-semibold text-ele-strong">{r.ref}</td>
                    <td className="px-3 py-2 text-ele-body">{formatDate(r.dateRaised)}</td>
                    <td className="max-md:hidden px-3 py-2 text-ele-body">{SOURCE_LABEL[r.source]}</td>
                    <td className="max-md:hidden px-3 py-2 text-ele-body">{r.category || "-"}</td>
                    <td className="cell-actions px-3 py-2">
                      <StatusPill domain="complaintSeverity" status={r.severity} />
                    </td>
                    <td className="cell-actions px-3 py-2">
                      <StatusPill domain="complaint" status={r.status} />
                    </td>
                    <td className="max-md:hidden px-3 py-2 text-ele-body">{r.customerName || "-"}</td>
                    <td className="max-md:hidden px-3 py-2 text-ele-body">{openDays}</td>
                    <td className={`max-md:hidden px-3 py-2 ${overdue ? "text-ele-danger" : "text-ele-body"}`}>
                      {formatDate(r.targetResolutionDate)}
                      {overdue ? " (overdue)" : ""}
                    </td>
                    <td className="cell-actions md:hidden pr-1 text-right">
                      <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                        <path d="M6 4l4 4-4 4z" />
                      </svg>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddComplaintModal
          onClose={() => setAdding(false)}
          onAdded={(id) => {
            setAdding(false);
            navigate(`/office/complaints/${id}`);
          }}
        />
      )}
    </div>
  );
}

function AddComplaintModal({ onClose, onAdded }: { onClose: () => void; onAdded: (id: string) => void }) {
  // The customer picker loads through the shared options loader so a failed fetch shows an inline
  // "Could not load. Retry" instead of a silently empty dropdown (Session AEH, FIX-2 B3).
  const [form, setForm] = useState({
    source: "customer" as ComplaintSource,
    severity: "medium" as ComplaintSeverity,
    customerId: "",
    description: "",
    category: "",
    targetResolutionDate: "",
    dateRaised: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set =
    (name: keyof typeof form) =>
    (e: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [name]: e.target.value }));

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form.description.trim()) {
      setErr("Describe what happened.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const created = await api.complaints.create({
        source: form.source,
        severity: form.severity,
        customerId: form.customerId || null,
        description: form.description.trim(),
        category: form.category.trim() || null,
        targetResolutionDate: form.targetResolutionDate || null,
        dateRaised: form.dateRaised || null,
        notes: form.notes.trim() || null,
      });
      onAdded(created.id);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message || e2.code : "Could not save the complaint. Please try again.");
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="New complaint" onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <form onSubmit={save}>
        <h2 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">New complaint</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <label>
            <span className={labelCls}>Source</span>
            <select aria-label="Source" value={form.source} onChange={set("source")} className={`${inputCls} w-full`}>
              {(["customer", "third_party", "internal", "regulatory"] as ComplaintSource[]).map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className={labelCls}>Severity</span>
            <select aria-label="Severity" value={form.severity} onChange={set("severity")} className={`${inputCls} w-full`}>
              {(["low", "medium", "high", "critical"] as ComplaintSeverity[]).map((s) => (
                <option key={s} value={s}>
                  {SEVERITY_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
          <div className="sm:col-span-2">
            <RecordPicker
              kind="customer"
              label="Customer (optional)"
              value={form.customerId}
              placeholder="Search customers (leave empty if not in the system)"
              onChange={(id) => setForm((f) => ({ ...f, customerId: id }))}
            />
          </div>
          <label className="sm:col-span-2">
            <span className={labelCls}>What happened</span>
            <textarea
              aria-label="Description"
              value={form.description}
              onChange={set("description")}
              rows={3}
              className={`${inputCls} w-full`}
            />
          </label>
          <label>
            <span className={labelCls}>Category</span>
            <input
              aria-label="Category"
              list="complaint-categories"
              value={form.category}
              onChange={set("category")}
              className={`${inputCls} w-full`}
            />
            <datalist id="complaint-categories">
              {COMPLAINT_CATEGORIES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </label>
          <label>
            <span className={labelCls}>Date raised</span>
            <input aria-label="Date raised" type="date" value={form.dateRaised} onChange={set("dateRaised")} className={`${inputCls} w-full`} />
          </label>
          <label>
            <span className={labelCls}>Target resolution date</span>
            <input
              aria-label="Target resolution date"
              type="date"
              value={form.targetResolutionDate}
              onChange={set("targetResolutionDate")}
              className={`${inputCls} w-full`}
            />
          </label>
          <label className="sm:col-span-2">
            <span className={labelCls}>Notes (optional)</span>
            <textarea aria-label="Notes" value={form.notes} onChange={set("notes")} rows={2} className={`${inputCls} w-full`} />
          </label>
        </div>
        {err && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {err}
          </p>
        )}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}
          >
            Cancel
          </button>
          <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Saving..." : "Create complaint"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
